// AI extraction of structured lab results from an uploaded lab report (PDF/photo),
// so the labAnalysis PDF path can populate `results[]` (Beluga requires ≥1 row) and
// stay on /lab/receiveLabResults. BE endpoint (Gemini, server-side key):
//   POST ${REACT_APP_BACKEND_URL}/ai/extract-lab-results  { mime, data(base64) }
//     → { results: [ {screeningDate,testName,testResult,testResultUnits,refRange,
//                      statusIndicator(H/L/N/N-A),reportDate,sampleSource} ] }
// Rows come pre-sanitized to Beluga's schema, focused on abnormal (H/L) results.
// See docs/FE_PRD_LABANALYSIS_PDF_EXTRACT.md (BE PR #230).
//
// The extracted rows are an AI CONVENIENCE SUMMARY — the patient MUST confirm/edit
// them before submit, and the full document is also relayed to the provider. Every
// failure soft-degrades to [] so the patient falls back to manual entry (never a
// dead end).

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// BE-accepted mime types (15MB decoded cap, enforced server-side).
const ALLOWED_MIME = new Set([
  'application/pdf', 'image/jpeg', 'image/png', 'image/heic', 'image/heif', 'image/webp',
]);

// Split a File-upload preview data URL ("data:<mime>;base64,<data>") into parts.
// Falls back to a supplied fileType when the data URL has no mime.
export const dataUrlToParts = (dataUrl, fileType) => {
  const m = /^data:([^;]*);base64,(.*)$/.exec(dataUrl || '');
  if (m) return { mime: m[1] || fileType || '', data: m[2] };
  // No data URL prefix — treat the whole string as base64 with the given type.
  if (dataUrl && fileType) return { mime: fileType, data: String(dataUrl).split(',').pop() };
  return null;
};

// Resolves to a results[] array (possibly empty). Never throws except on abort.
export const extractLabResults = async ({ mime, data }, signal) => {
  if (!BACKEND_URL || !mime || !data || !ALLOWED_MIME.has(mime)) return [];
  try {
    const response = await fetch(`${BACKEND_URL}/ai/extract-lab-results`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal,
      body: JSON.stringify({ mime, data }),
    });
    if (!response.ok) return []; // 503/501/429/400 → manual fallback
    const json = await response.json();
    return Array.isArray(json?.results) ? json.results : [];
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    return [];
  }
};
